import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { savingsService } from '../../services/savingsService';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, ClockIcon } from '@heroicons/react/24/outline';

const SavingsTransaction = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('transactions');
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [reversingTxn, setReversingTxn] = useState(null);

  // Reverse deposit mutation
  const reverseDepositMutation = useMutation(
    ({ transactionId, reason }) => savingsService.reverseDeposit(transactionId, reason),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['savings-account', accountId]);
        queryClient.invalidateQueries(['savings-transactions', accountId]);
        toast.success('Deposit reversed successfully!');
        setShowReverseModal(false);
        setReverseReason('');
        setReversingTxn(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to reverse deposit');
      }
    }
  );
  const openReverseModal = (txn) => {
    setReversingTxn(txn);
    setReverseReason('');
    setShowReverseModal(true);
  };

  const handleReverseDeposit = (e) => {
    e.preventDefault();
    if (!reverseReason || reverseReason.length < 5) {
      toast.error('Please provide a valid reason (min 5 chars)');
      return;
    }
    reverseDepositMutation.mutate({ transactionId: reversingTxn.id, reason: reverseReason });
  };

  // Fetch account details
  const { data: accountData, isLoading: accountLoading } = useQuery(
    ['savings-account', accountId],
    () => savingsService.getAccountDetails(accountId)
  );

  // Fetch transactions
  const { data: transactionsData, isLoading: transactionsLoading, refetch } = useQuery(
    ['savings-transactions', accountId, fromDate, toDate],
    () => savingsService.getStatement(accountId, fromDate, toDate),
    { enabled: !!accountId }
  );

  // Deposit mutation
  const depositMutation = useMutation(
    (data) => savingsService.deposit(accountId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['savings-account', accountId]);
        queryClient.invalidateQueries(['savings-transactions', accountId]);
        toast.success('Deposit successful!');
        setDepositAmount('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Deposit failed');
      }
    }
  );

  // Withdraw mutation
  const withdrawMutation = useMutation(
    (data) => savingsService.withdraw(accountId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['savings-account', accountId]);
        queryClient.invalidateQueries(['savings-transactions', accountId]);
        toast.success('Withdrawal successful!');
        setWithdrawAmount('');
        setWithdrawReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Withdrawal failed');
      }
    }
  );

  const handleDeposit = (e) => {
    e.preventDefault();
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    depositMutation.mutate({
      amount: parseFloat(depositAmount),
      payment_method: 'cash',
      remarks: 'Over-the-counter deposit'
    });
  };

  const handleWithdraw = (e) => {
    e.preventDefault();
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!withdrawReason) {
      toast.error('Please provide a reason for withdrawal');
      return;
    }
    withdrawMutation.mutate({
      amount: parseFloat(withdrawAmount),
      reason: withdrawReason,
      payment_method: 'cash'
    });
  };

  const account = accountData?.data;
  const transactions = transactionsData?.data || [];
  const totalDeposits = transactions
    .filter(t => t.transaction_type === 'deposit' && t.status === 'active')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalWithdrawals = transactions
    .filter(t => t.transaction_type === 'withdraw')
    .reduce((sum, t) => sum + t.amount, 0);

  if (accountLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="p-6 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800">Account Not Found</h3>
          <button
            onClick={() => navigate('/savings')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Savings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/savings')}
          className="text-blue-600 hover:text-blue-800 mb-2 inline-block"
        >
          ← Back to Savings Accounts
        </button>
        <h1 className="text-2xl font-bold">Savings Account Transactions</h1>
        <p className="text-gray-600">Account: {account.account_no}</p>
      </div>

      {/* Account Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Current Balance</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(account.current_balance)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Scheme Type</div>
          <div className="text-lg font-semibold capitalize">{account.scheme_type?.replace('_', ' ')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Interest Rate</div>
          <div className="text-lg font-semibold">{account.interest_rate}%</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Status</div>
          <div className="mt-1">
            <span className={`px-2 py-1 text-sm rounded-full ${getStatusColor(account.status)}`}>
              {account.status}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-4">
          <button
            onClick={() => setActiveTab('transactions')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'transactions'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ClockIcon className="h-5 w-5 inline mr-2" />
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('deposit')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'deposit'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ArrowDownTrayIcon className="h-5 w-5 inline mr-2" />
            Deposit
          </button>
          <button
            onClick={() => setActiveTab('withdraw')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'withdraw'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ArrowUpTrayIcon className="h-5 w-5 inline mr-2" />
            Withdraw
          </button>
        </nav>
      </div>

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg shadow">
          {/* Date Filter */}
          <div className="p-4 border-b flex gap-4">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border rounded-lg"
              placeholder="From Date"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border rounded-lg"
              placeholder="To Date"
            />
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply Filter
            </button>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 border-b">
            <div className="text-center">
              <div className="text-sm text-gray-500">Total Deposits</div>
              <div className="text-lg font-bold text-green-600">{formatCurrency(totalDeposits)}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500">Total Withdrawals</div>
              <div className="text-lg font-bold text-red-600">{formatCurrency(totalWithdrawals)}</div>
            </div>
          </div>

          {/* Transactions Table */}
          {transactionsLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No transactions found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Running Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.transaction_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          transaction.transaction_type === 'deposit'
                            ? 'bg-green-100 text-green-800'
                            : transaction.transaction_type === 'withdraw'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {transaction.transaction_type}
                        </span>
                        {transaction.transaction_type === 'deposit' && transaction.status !== 'reversed' && (
                          <button
                            className="ml-2 text-xs text-red-600 underline hover:text-red-800"
                            onClick={() => openReverseModal(transaction)}
                            title="Reverse Deposit"
                          >
                            Reverse
                          </button>
                        )}
                        {transaction.status === 'reversed' && (
                          <span className="ml-2 text-xs text-gray-400">(reversed)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {transaction.payment_method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(transaction.running_balance)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {transaction.remarks || '-'}
                        {transaction.status === 'reversed' && transaction.reversal_reason && (
                          <div className="text-xs text-red-400 mt-1">Reason: {transaction.reversal_reason}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                      {/* Reverse Deposit Modal */}
                      <Dialog open={showReverseModal} onClose={() => setShowReverseModal(false)} className="fixed z-10 inset-0 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4">
                          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 z-20 relative">
                            <Dialog.Title className="text-lg font-bold mb-2">Reverse Deposit</Dialog.Title>
                            <form onSubmit={handleReverseDeposit} className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium mb-1">Reason for reversal</label>
                                <textarea
                                  className="w-full border rounded px-3 py-2"
                                  value={reverseReason}
                                  onChange={e => setReverseReason(e.target.value)}
                                  minLength={5}
                                  required
                                  placeholder="Enter reason (required)"
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                                  onClick={() => setShowReverseModal(false)}
                                  disabled={reverseDepositMutation.isLoading}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="submit"
                                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                                  disabled={reverseDepositMutation.isLoading}
                                >
                                  {reverseDepositMutation.isLoading ? 'Reversing...' : 'Reverse Deposit'}
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      </Dialog>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Deposit Tab */}
      {activeTab === 'deposit' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Make a Deposit</h2>
          <form onSubmit={handleDeposit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Deposit Amount</label>
              <input
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Payment Method</label>
              <select className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="cash">Cash</option>
                <option value="mobile_banking">Mobile Banking</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={depositMutation.isLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {depositMutation.isLoading ? 'Processing...' : 'Deposit'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Withdraw Tab */}
      {activeTab === 'withdraw' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Make a Withdrawal</h2>
          <form onSubmit={handleWithdraw} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Withdrawal Amount</label>
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Available balance: {formatCurrency(account.current_balance)}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Reason for Withdrawal</label>
              <textarea
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                rows="3"
                placeholder="Please provide a reason..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={withdrawMutation.isLoading}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {withdrawMutation.isLoading ? 'Processing...' : 'Withdraw'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SavingsTransaction;